/* tslint:disable:jsx-key */
import { LoginType } from '@mm/state/controllers/SessionController';
import JSONUtils from '@mm/state/helpers/json-utils';
import { selectInviteToken } from '@mm/state/selectors/userSelector';
import { LocalStorageService } from '@mm/utils';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { Route, useParams } from 'react-router-dom';
import useRouter from 'use-react-router';
import { PREV_LOCATION } from '../../common/helpers/routes/GuardedPrivateRoute';
import css from './AuthRouter.module.css';
import { LoginForm, LoginFormProps } from './components/LoginForm/LoginForm';
import { LoginLayout } from './components/LoginLayout/LoginLayout';
import { ResetPasswordForm } from './components/ResetPasswordForm/ResetPasswordForm';
import {
  SignUpForm,
  SignUpFormProps,
} from './components/SignUpForm/SignUpForm';
import { routes } from './routes';
import { useAmbassadorId } from './use-ambassador-id';
import { useAuthenticatedRedirect } from './use-authenticated-redirect';
import { useInviteQuery } from './use-invite-query';

const SignUp = () => {
  const { type } = useParams<{ type?: LoginType }>();
  const { companyId, companyName } = useInviteQuery();

  const stringifiedPrevLocation = LocalStorageService.getItem(PREV_LOCATION);
  const prevLocation = JSONUtils.safeParse<Location>(stringifiedPrevLocation);
  const parsedPreviousPathname = prevLocation?.pathname?.split('/');
  const isCustomPlan =
    parsedPreviousPathname?.[1] === 'subscribe' &&
    !!parsedPreviousPathname?.[2];

  const { match } = useRouter();
  const isAgencySignUp =
    match.path.includes(routes.signup.path) && type === LoginType.agency;

  useAuthenticatedRedirect();
  const affiliateId = useAmbassadorId();

  const inviteToken = useSelector(selectInviteToken);

  let props: SignUpFormProps = {
    type,
    inviteToken,
    heading: isCustomPlan
      ? 'WELCOME TO CUSTOMERS.AI!'
      : 'Convert traffic into sales today',
    aboveHeading: '',
    css: {
      form: classNames(css.form, {
        [css.whitelabelForm]: !!companyId,
        [css.agencieForm]: isAgencySignUp,
      }),
      container: css.container,
      heading: classNames(css.heading, {
        [css.headingAgencies]: isAgencySignUp,
        [css.invitesHeading]: inviteToken,
      }),
      subheading: classNames({ [css.subheading]: !(inviteToken || companyId) }),
      aboveHeading: css.aboveHeadingContainer,
      submitBtn: type === LoginType.funnelpro ? css.submitPurple : css.submit,
    },
    requiredOnlyFields: true,
    withPhone: true,
    withPasswordConfirmation: false,
    withCompanyName: true,
    withWebsite: true,
  };

  if (inviteToken) {
    props = {
      ...props,
      heading: 'WELCOME TO CUSTOMERS.AI!',
      subheading: `You’ve been invited to join ${companyName}`,
      requiredOnlyFields: true,
      inviteToken,
    };
  }

  let layoutType = type;

  if (inviteToken) {
    layoutType = LoginType.invite;
  }

  // not every affiliate, approved list only
  if (affiliateId) {
    layoutType = LoginType.ambassador;
  }

  if (type === LoginType.highLevel) {
    props = {
      ...props,
      heading: 'Identify website visitors today',
    };
  }

  if (companyId) {
    const companyNameToUse = Array.isArray(companyName)
      ? companyName[0]
      : companyName;

    layoutType = LoginType.whiteLabel;

    props = {
      ...props,
      heading: `WELCOME TO ${companyNameToUse.toUpperCase()}`,
      subheading: '',
      invitingCompanyId: Number(companyId),
      invitingCompanyName: companyNameToUse,
      requiredOnlyFields: true,
      type: layoutType,
    };
  }

  return (
    <LoginLayout
      type={layoutType}
      affiliateId={affiliateId}
      companyId={Number(companyId)}
      hideTrial={isCustomPlan}
    >
      <SignUpForm {...props} hideTrial={isCustomPlan} showFacebook={false} />
    </LoginLayout>
  );
};

const SignIn = () => {
  const inviteToken = useSelector(selectInviteToken);
  const { type } = useParams<{ type?: LoginType }>();
  const { companyId, companyName } = useInviteQuery();
  const { match } = useRouter();
  const isAgencySignIn =
    match.path.includes(routes.signin.path) && type === LoginType.agency;

  useAuthenticatedRedirect();

  let layoutType = inviteToken ? LoginType.invite : type;
  let props: LoginFormProps = {
    type,
    css: {
      container: classNames(css.form, {
        [css.whitelabelForm]: !!companyId,
        [css.agencieForm]: isAgencySignIn,
      }),
      heading: classNames(css.heading, {
        [css.headingAgencies]: isAgencySignIn,
        [css.invitesHeading]: inviteToken,
      }),
      subheading: classNames({ [css.subheading]: !(inviteToken || companyId) }),
    },
    heading: 'Log In',
    subheading: '',
    inviteToken,
  };

  if (inviteToken) {
    props = {
      ...props,
      heading: 'WELCOME TO CUSTOMERS.AI!',
      subheading: `You’ve been invited to join ${companyName}`,
      withFacebookLogin: !inviteToken,
      inviteToken,
    };
  }

  if (companyId) {
    const companyNameToUse = Array.isArray(companyName)
      ? companyName[0]
      : companyName;

    layoutType = LoginType.whiteLabel;

    props = {
      ...props,
      heading: `WELCOME TO ${companyNameToUse.toUpperCase()}`,
      subheading: '',
      type: layoutType,
      withFacebookLogin: !inviteToken,
      invitingCompanyId: Number(companyId),
      invitingCompanyName: companyNameToUse,
    };
  }

  return (
    <LoginLayout type={layoutType} companyId={Number(companyId)}>
      <LoginForm {...props} />
    </LoginLayout>
  );
};

const ResetPassword = () => {
  const inviteToken = useSelector(selectInviteToken);
  const { type } = useParams<{ type?: LoginType }>();
  useAuthenticatedRedirect();

  return (
    <LoginLayout type={inviteToken ? LoginType.invite : type}>
      <ResetPasswordForm type={type} className={css.form} />
    </LoginLayout>
  );
};

export const AuthRouter = [
  <Route key="route_signup" path={routes.signup.path} component={SignUp} />,
  <Route key="route_signin" path={routes.signin.path} component={SignIn} />,
  <Route
    key="route_reset"
    path={routes.resetPassword.path}
    component={ResetPassword}
  />,
];
